import { isYesCode } from '../../lib/boolCode';
import type { PayslipLine } from './payslipTypes';

/**
 * Pure rubrique domain logic for the payslip detail — category derivation,
 * default visibility, base selection and ordering. Kept free of React/theme so
 * it is unit-testable in isolation; colours/labels live in the presentation map
 * (`config/payslipRubriquePresentation.ts`).
 */

/** The five payslip-line categories, in pay-slip display order. */
export type RubriqueCategory =
  | 'gains'
  | 'employeeCharges'
  | 'employerCharges'
  | 'stc'
  | 'calculation';

/** Category display order (Gains → Employee → Employer → STC → Calculation). */
export const RUBRIQUE_CATEGORY_ORDER: readonly RubriqueCategory[] = [
  'gains',
  'employeeCharges',
  'employerCharges',
  'stc',
  'calculation',
];

/**
 * Derive a line's category from its raw `rubriqueparam` flags. Precedence follows
 * the spec order; a line with no category flag set is a (non-financial) calculation
 * rubrique.
 */
export function resolveRubriqueCategory(line: PayslipLine): RubriqueCategory {
  if (isYesCode(line.gain)) return 'gains';
  if (isYesCode(line.retenue)) return 'employeeCharges';
  if (isYesCode(line.retenuepatronale)) return 'employerCharges';
  if (isYesCode(line.soldetoutcompte)) return 'stc';
  return 'calculation';
}

/** A line is shown by default only when `visible = 'O'` (unless "show all" is on). */
export function isRubriqueVisible(line: PayslipLine): boolean {
  return isYesCode(line.visible);
}

/** A line active for this specific bulletin (`rubriquebulletin.actif_bulletin = 'O'`). */
export function isRubriqueActive(line: PayslipLine): boolean {
  return isYesCode(line.actif_bulletin);
}

/**
 * Inverse of {@link isRubriqueActive} — kept as its own predicate so styling call
 * sites (row greyed-out wherever an inactive line is rendered) read naturally
 * without a negation.
 */
export function isRubriqueInactive(line: PayslipLine): boolean {
  return !isRubriqueActive(line);
}

/** The base value that fed the calculation: the date base for VARDAT, else numeric. */
export function rubriqueBase(line: PayslipLine): {
  value: string | number | null;
  isDate: boolean;
} {
  if (line.type === 'VARDAT') return { value: line.base_date_bulletin, isDate: true };
  return { value: line.base_bulletin, isDate: false };
}

/**
 * The rubriques to display, in ascending pay-plan order (`numeroordre`), matching
 * the order the backend sends. Lines with no `numeroordre` sort last; ties keep
 * their incoming order (stable sort). When `showAll` is false, only default-visible
 * AND active lines (`visible='O'` AND `actif_bulletin='O'` — Homère Classic's own
 * default filter) are kept; "show all" reveals both the non-visible and the
 * inactive ones, the latter still rendered greyed-out ({@link isRubriqueInactive}).
 * No subtotal/amount is ever recomputed — the payslip is frozen in Homère Classic
 * and shown as synced; category is a per-row attribute
 * ({@link resolveRubriqueCategory}), not a grouping.
 */
export function orderedRubriques(lines: PayslipLine[], showAll = false): PayslipLine[] {
  const kept = showAll
    ? lines
    : lines.filter(line => isRubriqueVisible(line) && isRubriqueActive(line));
  return [...kept].sort((a, b) => {
    const ao = a.numeroordre ?? Number.POSITIVE_INFINITY;
    const bo = b.numeroordre ?? Number.POSITIVE_INFINITY;
    return ao - bo;
  });
}
